// Which babies this pregnancy is still carrying, and which one did not continue (KIN-2066).
//
// A lower foetus count from the maternity hospital almost always means a foetus existed and did
// not continue — a fetal demise, a vanishing twin — so what the GP records here is an outcome,
// never a deletion. The backend keeps the row, keeps every observation already recorded against
// it, and lowers the pregnancy's declared count to the remaining live foetuses.

/**
 * The foetuses this pregnancy is still carrying, in identifier order.
 *
 * `isActive` is the same flag the visit form keys its Foetal Exam sections off, so the dialog
 * offers exactly the babies a new visit would ask about.
 */
export const findLiveFoetuses = (pregnancy) =>
  (pregnancy?.foetuses ?? [])
    .filter((foetus) => foetus?.isActive === true)
    .sort((one, other) =>
      (one.foetusIdentifier ?? "").localeCompare(other.foetusIdentifier ?? "")
    );

/**
 * The foetuses already recorded as not continuing, as `{ id, foetusIdentifier, outcome }`.
 *
 * Read out of the pregnancy's antenatal visits rather than off the pregnancy itself, because
 * `pregnancy.foetuses` is an active-only read on the backend: an ended foetus leaves it the moment
 * it ends, so the pregnancy alone can no longer say that Baby B ever existed. Each visit's
 * `foetalObservations` are fetched by the observation's own flag and carry the whole foetus with
 * them — including the ended ones and their outcome — which is what keeps the history readable.
 *
 * The consequence worth knowing: a foetus ended before any observation was ever recorded against
 * it has nothing to be found in, and will not be listed. Naming the outcomes we can account for is
 * better than naming none, and a `pregnancy.foetuses` that could include the ended ones is a
 * backend change, not something to guess at here.
 */
export const findEndedFoetuses = (anteNatalConsultations) => {
  const byId = new Map();

  (anteNatalConsultations ?? []).forEach((consultation) => {
    (consultation?.foetalObservations ?? []).forEach((observation) => {
      const foetus = observation?.pregnancyFoetus;

      if (!foetus?.id || foetus.isActive !== false) return;

      byId.set(foetus.id, {
        id: foetus.id,
        foetusIdentifier: foetus.foetusIdentifier ?? "",
        outcome: foetus.outcome ?? "",
      });
    });
  });

  return [...byId.values()].sort((one, other) =>
    one.foetusIdentifier.localeCompare(other.foetusIdentifier)
  );
};

/** "Baby B", or "This foetus" when the backend gave us no identifier to name it by. */
export const describeFoetus = (foetus) =>
  foetus?.foetusIdentifier ? `Baby ${foetus.foetusIdentifier}` : "This foetus";
